import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { serverTimestamp, getDocFromServer } from 'firebase/firestore';
import { setDoc, hasLiveWriteTo } from './writes';
import { docRef, COLLECTIONS, SETTINGS_DOCS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { normaliseStudio, studioDisplayName, isStudioNamed } from '../domain/studio';
import { studioSave } from '../domain/studioSettings';
import { useDocumentWindow } from './useDocumentWindow';
import { zoneToRecord } from '../domain/timeZone';
import { browserTimeZone } from '../lib/intlOptions';

// The studio's own profile: `users/{uid}/settings/studio`.
//
// The path and field names are a contract: `getStudio` in
// functions/delivery/firestoreStore.js reads this document, and `studioNameFor`
// in functions/delivery/facts.js reads `.name` off it, falling back to the
// deployment's from-name when it is absent.
//
// One document, read live like every other provider here, so a change made on
// a phone shows up on the laptop without a refresh.

const SettingsContext = createContext(null);

// ─── What a save sends ─────────────────────────────────────────────────────
//
// `studioSave` in src/domain/studioSettings.js is the merge
// (`mergeStudioPatch`), the normalising and the decision of what to send
// (`studioFieldsToWrite`) in one call, so a phone composes a patch the same
// way.
//
// **A patch naming a group changes those fields of it**, not the whole group:
// a panel builds its patch from its last render, so two checkboxes ticked
// faster than a snapshot round-trip would otherwise both build on the same
// stale group and the second would undo the first. Top-level scalars — name,
// phone, address, locale, timeZone — need nothing: `{ ...base, ...patch }`
// leaves the rest alone. Every caller patches one field at a time (see
// SettingsPage.jsx).
//
// **A merging set naming only the fields the patch names (#337).** A whole
// composed record would let device A's offline `invoicing.showLocation` and
// device B's offline `timeZone` overwrite each other. `settings` is
// `perFieldMerge` in `core/src/reconcile.rs`, and
// `test/core/wholeDocumentSets.test.js` counts whole-document sets here.
//
// **A group goes by the leaf (#344)**: `{ invoicing: { showLocation: true } }`
// is sent as exactly that, so two devices editing two leaves of one group both
// survive. `{ merge: true }` merges a nested map per leaf, recursively,
// asserted in `test/integration/writeQueueFacade.test.jsx` one level down and
// two. The overlay folds by top-level field, so `composePendingMaps` in
// `src/data/overlay.js` composes the nested map for display, after the queue
// and never in the write (#310's shape); otherwise the leaves not touched would
// read as defaults until a snapshot arrived. The rules accept it because
// `request.resource.data` on a merge is the **merged** document, so
// `validStudio` still reads a whole studio record.
//
// Not a dotted field path: through this queue a dotted key is a literal
// top-level field name with a dot in it, asserted in the same file.
//
// ── Except on a create, where it has to be whole ──────────────────────────
//
// `validStudio` in `firestore.rules` reads `d.locale`, `d.nameFormats.teacher`,
// `d.invoicing` and six more with no guards. Onto a record that does not
// exist, the merged document is the one field and the rule fails on the first
// property it cannot find, so a create carries the whole normalised record.
// The core cannot make this create-versus-overwrite distinction (it cannot see
// a document); it is made here, where the read is. The kind stays `merge`
// either way, so nothing raises `wholeDocumentSet`.
//
// ── `stored` is not the read's answer alone (#345) ────────────────────────
//
// It is the server's answer (`exists`) **or** a write this device has queued
// for this path and the server has not answered (`hasLiveWriteTo` in
// `./writes.js`). The store arm's `exists` already folds the queue; the
// snapshot arm cannot, so after a reload while offline it would send the whole
// record again, composed from `normaliseStudio(null)`: defaults, over the
// fields the first write had set. See `saveStudio`.

// ─── "Have we read this studio's record yet?" ──────────────────────────────
//
// On a cold load `user` is null for a moment while Firebase resolves the
// session, and `normaliseStudio(null)` is the full set of defaults,
// indistinguishable from a studio that has stored nothing. A provider that
// reported "loaded" in that window would let a settings control write those
// defaults over the studio's own record.
//
// So "the document" and "whose document" are one value — `{ uid, data }` —
// and the rest is derived from it:
//
//   hasAnswer  there is an answer in hand and it is *this* user's. Not
//            `hasRead` (#279): "the server has answered" and "there is
//            something to show" are different questions and this provider
//            wants the second.
//   loading  derived, never assigned, so it cannot drift out of step with
//            whether the record has been read. It means "the value you are
//            holding is not this studio's record".
//   latest   the same fact readable synchronously, for `saveStudio`, which
//            needs it at call time and not at the render time of whatever
//            closure the click arrived through.

// ─── The record a new studio starts with (#462) ────────────────────────────
//
// Written once, at sign-up, beside `createOnboardingDoc` (src/pages/
// LoginPage.jsx), so the studio's Region is the browser's rather than
// `DEFAULT_LOCALE`. `signupLocale` in src/domain/locale.js says which tag, and
// why this is stored rather than inferred on every read.
//
// **The whole normalised record**, because this is a create (see "Except on a
// create" above). `studioSave(…, stored = false)` is the decision `saveStudio`
// makes for a studio's first save, so the two write one shape. It is a merging
// set, so it is not counted by `test/core/wholeDocumentSets.test.js`.
//
// A studio whose sign-up write is absent has no document and reads as `en-US`
// on every device.
//
// **And the studio's time zone, from this browser too (#620)**, when the
// browser names one `zoneToRecord` accepts: a new studio's first grant,
// reminder or upload is then dated on its own calendar, and this whole-record
// create carries `timeZone` either way — without the zone it would write
// `null` over one `recordStudioTimeZone` had just queued, if the two crossed
// on a first load.
export async function createStudioSettings(uid, locale, timeZone = null) {
  if (!db || !uid) return;
  const zone = zoneToRecord(null, timeZone);
  const { fields } = studioSave(normaliseStudio(null), zone ? { locale, timeZone: zone } : { locale }, false);
  await setDoc(
    docRef(uid, COLLECTIONS.settings, SETTINGS_DOCS.studio),
    { ...fields, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

// ─── A studio with no time zone is given its device's (#620) ───────────────
//
// Every server fold that dates something on the studio's calendar reads
// `settings/studio.timeZone` and, without one, the UTC day: billing's grants
// and access window (`functions/billing/studioDay.js`, #617), the reminder
// worker's "today" and send window (`studioToday`/`studioHour` in
// src/domain/reminders.js), and a library upload's `uploadDay` (#584). The
// screens read this device's day, so from 17:00 in Los Angeles the two would
// disagree.
//
// **Without asking**: the profile drawer already shows this device's zone as
// the selected value for a studio with none, and choosing the option already
// chosen is not a change, so recording it makes the server agree with the
// screen. The drawer, MMS's Edit Studio Profile, is unchanged.
//
// **Never over a zone somebody chose** — three guards:
//
//   * **The server's answer, not this device's** (`getDocFromServer`). The
//     provider's read can be a previous session's (`stale`) or the SDK's
//     persistent cache, and either can be from before a zone was picked on
//     another device. Offline it rejects, nothing is written, and the next
//     load asks again.
//   * **Nothing of ours still owed to the document** (`hasLiveWriteTo`, asked
//     before and after the read). A zone picked in the drawer offline is in
//     the queue and not on the server, so the server would say "none" and
//     this write would queue behind the teacher's and replace it. The same
//     check keeps this out of the way of sign-up's whole-record create.
//   * **`zoneToRecord`'s rule** (core/src/time_zone.rs): only an absent zone
//     is filled, and a device on UTC records nothing.
//
// Left open: a zone picked on a second device between the server's answer and
// this write arriving is replaced — this layer has no transactions
// (useLedger.jsx says why). The teacher sees the zone in the profile drawer
// and can pick it again.
//
// **A studio with no document at all** gets the whole default record with the
// zone, as its first save from any panel would, with locale `en-US`, which
// every device already reads it as. A sign-up in the same session is the one
// write that could cross this; it carries the same zone
// (`createStudioSettings` above), so the zone survives either way.
//
// Resolves the zone it recorded, or null when there was nothing to record or
// something of ours was owed. Rejects when the server could not be asked.
export async function recordStudioTimeZone(uid, deviceZone) {
  if (!db || !uid || !zoneToRecord(null, deviceZone)) return null;
  const path = docRef(uid, COLLECTIONS.settings, SETTINGS_DOCS.studio).path;
  if (await hasLiveWriteTo(path)) return null;
  const snap = await getDocFromServer(docRef(uid, COLLECTIONS.settings, SETTINGS_DOCS.studio));
  const stored = snap.exists() ? snap.data() : null;
  const zone = zoneToRecord(stored?.timeZone, deviceZone);
  if (!zone || await hasLiveWriteTo(path)) return null;
  const { fields } = studioSave(normaliseStudio(stored), { timeZone: zone }, snap.exists());
  await setDoc(
    docRef(uid, COLLECTIONS.settings, SETTINGS_DOCS.studio),
    { ...fields, updatedAt: serverTimestamp() },
    { merge: true },
  );
  return zone;
}

// `recordStudioTimeZone`, once per studio per page load, and only when this
// device's picture of the record has no zone: a studio that has one never
// costs a server read. Mounted once, under SettingsProvider in App.jsx — not
// inside the provider, so a test that mounts the provider is not also
// testing this.
//
// The provider's `loading` is the gate, as it is for every save: before the
// first read the record in hand is the defaults, whose zone is always null.
export function useRecordStudioTimeZone(deviceZone = browserTimeZone()) {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const { loading, settings } = useSettings();
  const stored = settings.timeZone;
  const asked = useRef(null);
  useEffect(() => {
    if (!uid || loading || asked.current === uid) return;
    if (!zoneToRecord(stored, deviceZone)) return;
    asked.current = uid;
    recordStudioTimeZone(uid, deviceZone).then(undefined, (err) => {
      console.warn(
        "[time zone] could not ask the server for the studio's time zone, so this device's "
        + `(${deviceZone}) was not recorded; the next load asks again:`,
        err,
      );
    });
  }, [uid, loading, stored, deviceZone]);
}

export function SettingsProvider({ children }) {
  // `authLoading`: without it a signed-out studio and a session that has not
  // resolved yet are both `user == null`, and the provider would report
  // "loaded, nothing stored" for the second of them.
  const { user, loading: authLoading } = useAuth();
  const uid = user?.uid || null;

  // The same fact, readable synchronously.
  //
  // `saveStudio` composes its patch onto the record in hand, so it needs the
  // *current* state, not the value closed over by a render: two checkboxes
  // toggled faster than a snapshot round-trip would otherwise both build on the
  // same stale object. A ref is read at call time, so successive saves compose.
  //
  // **`{ record, stored }`, one value** (#337). The record is what a patch
  // composes onto; `stored` is whether there is a document to merge into, which
  // decides whether the write names its fields or carries the whole shape (see
  // `studioFieldsToWrite`).
  //
  // Null means "not read yet", the state a save must refuse to build on. It is
  // set only by a read arriving and a save being issued, never from a render,
  // because a render can happen between those two with the pre-save value
  // still in props. A save sets it from the read when the effect has not run
  // yet (#349): the effect runs *after* a child's effects, so a consumer saving
  // on mount would otherwise find it null on the tick `loading` first went
  // false.
  //
  // The two writers are ordered by the runtime rather than by a lock: a save
  // assigns this and calls setDoc in one synchronous block, so no read can
  // interleave, and the write lands locally before anything fires, so every
  // read that follows already contains it.
  const latest = useRef(null);

  // ── #207 U5: `users/{uid}/settings/studio`, from whichever path serves ────
  //
  // A read path that cannot tell *we have never been told* from *we were told
  // it is absent* would hand `null` to `normaliseStudio` on a cold offline open,
  // and its `en-US` default would change every amount in a British studio.
  // `hasAnswer` is the distinction, made in `useDocumentWindow`.
  //
  // Keyed on the uid, not the user object: `onAuthStateChanged` can hand back
  // a fresh object for the same person, and resubscribing for that would drop
  // every panel back into its loading state for no reason.
  const read = useDocumentWindow({
    uid,
    collection: COLLECTIONS.settings,
    id: SETTINGS_DOCS.studio,
  });

  // `null` for a document that does not exist, which `normaliseStudio` turns
  // into the same object it makes from `{}`. A document that is not there still
  // counts as **read**: it is what a brand-new studio's first save builds on.
  //
  // **`hasAnswer`, not `hasRead`** (#279): a studio whose settings document
  // exists only as a write queued this session has a record, and gating the
  // panel on a server receipt would hide it (#267). It is never true before
  // the first read has come back.
  const { data, exists, hasAnswer } = read;

  // The same fact readable synchronously, for `saveStudio`.
  //
  // Never from a render: this effect runs only when the **read** moves, so a
  // render between `saveStudio` assigning this and the write coming back reads
  // the same `data` and does not re-run. On the store path the save's write
  // enters our queue and the overlay folds it, so the value that arrives here
  // already contains the save.
  //
  // `exists` rather than `data !== null`, per `useDocumentWindow`'s doc
  // comment, gated on `hasAnswer`: "we have not heard" must not read as "there
  // is no document there". A queued creation counts as existing, so the second
  // save of a brand-new studio merges onto the first.
  useEffect(() => {
    latest.current = hasAnswer
      ? { record: normaliseStudio(data), stored: Boolean(exists) }
      : null;
  }, [hasAnswer, data, exists]);

  const value = useMemo(() => {
    const settings = normaliseStudio(hasAnswer ? data : null);

    // Derived, never assigned. Signed out is *not* loading — there is nothing
    // to read and the defaults are the answer. Signed in with no read yet is.
    const loading = authLoading || (Boolean(uid) && !hasAnswer);

    return {
      loading,
      // `settings/studio` stopped before it answered (#647): Business
      // Settings says so rather than offering nothing for good.
      failed: Boolean(read.failed),
      settings,

      // Convenience readers, so a call site that only wants the name does not
      // have to know the record's shape or repeat the fallback. `name` is what
      // an invoice, a report or a receipt prints.
      name: studioDisplayName(settings),
      named: isStudioNamed(settings),
      nameFormats: settings.nameFormats,

      // The locale identifier — "en-US", "ko-KR". Currency, mileage units and
      // date/time format are *derived* from it and are not stored: MMS has no
      // currency setting, only Region, whose hint says it decides all three.
      // The derivation is src/domain/locale.js (`currencyFor(locale)` and
      // friends); this provider exposes the string and nothing more.
      locale: settings.locale,

      // One merging write, naming the fields the patch named. Two merges:
      //
      //   * the **patch** merges into the record in hand, one level deep into
      //     the named group (`mergeStudioPatch`), so two fields changed in
      //     quick succession are two patches composed onto `latest.current`;
      //   * the **write** merges into the document, naming only those fields
      //     (`studioFieldsToWrite`), because the same race happens between two
      //     devices and `latest.current` cannot see the other one (#337).
      //
      // A merging `setDoc` on a document that does not exist creates it, so
      // there is no seeding step — but the payload differs, because the rules
      // judge a create by the whole shape. `studioFieldsToWrite` decides that,
      // and the `latest.current` guard below is the one case that must not
      // create at all.
      saveStudio: async (patch = {}) => {
        if (!uid || !db) return false;

        // `latest.current`, not `settings`: see the ref above. A save that has
        // not yet come back from the snapshot must still be visible to the next
        // one, or the second toggle silently undoes the first.
        //
        // Null here means this studio's record has not been read, so the only
        // thing available to write would be the defaults, over whatever is
        // stored. This silent refusal is the backstop behind the `loading`
        // gate: it fires only for a caller that skipped the gate.
        //
        // ── `loading` false means a base can be built (#349) ───────────────
        //
        // `loading` is decided during render, and the effect that assigns the
        // ref runs later — React flushes a child's effects before its
        // parent's — so a consumer saving from an effect on the tick `loading`
        // first goes false would find the ref null. So the base is taken from
        // the ref when it is set, and otherwise computed from the same values
        // `loading` is computed from: `hasAnswer` false is exactly when
        // `loading` is true for a signed-in studio.
        //
        // This runs when a save is issued, one of the ref's two writers. It is
        // written only when null, so it cannot overwrite a newer composition,
        // and before the `await` below, so the far side has something to read.
        if (!latest.current && hasAnswer) {
          latest.current = { record: normaliseStudio(data), stored: Boolean(exists) };
        }
        if (!latest.current) return false;

        // ── Both arms decide *is this a create* the same way (#345) ────────
        //
        // `latest.current.stored` does not survive a reload: a studio that
        // saves offline, reloads, and saves again has a document queued and a
        // provider that has forgotten it. The store arm's `exists` folds the
        // queue; the snapshot arm's would say the server has nothing and send
        // the whole record **from the defaults** over the queued write (#345).
        // So *a write this device has queued and the server has not answered
        // counts as a document being there*, on both arms.
        //
        // Asked before `latest.current` is read again, so that the assignment
        // and the `setDoc` below stay one synchronous block.
        //
        // The second `docRef(…)` is inline in the `setDoc` below on purpose:
        // `test/core/wholeDocumentSets.test.js` reads these call sites
        // statically and attributes a write to a collection only when the ref
        // is built in the call (#337).
        const path = docRef(uid, COLLECTIONS.settings, SETTINGS_DOCS.studio).path;
        const queued = latest.current.stored ? true : await hasLiveWriteTo(path);

        // ── A whole record only where the server says there is none (#631) ──
        //
        // Neither "the read says absent" nor "nothing of ours is queued" proves
        // there is no document: a cold cache answers a fresh listener with
        // `exists: false`, offline and online alike, and the read store keeps
        // that as an answer. The create branch would then send the whole record
        // composed from `normaliseStudio(null)` — en-US, no name, default
        // formats — merged over the studio's real one. So this asks the
        // server, as `recordStudioTimeZone` above does (./serverConfirmed.js
        // has the argument):
        //
        //   * a document there → the patch composes onto **its** record and
        //     only the patch's fields are sent;
        //   * none there → the whole record;
        //   * the server cannot be asked → only the patch's fields. Onto a
        //     studio that has a record (#462, #620) that is the right write;
        //     onto one that has none, the rules refuse it and the refusal is
        //     reported, rather than defaults written over a record this device
        //     never saw.
        let serverRecord = null;
        let serverSaysAbsent = false;
        if (!latest.current?.stored && !queued) {
          const answer = await getDocFromServer(docRef(uid, COLLECTIONS.settings, SETTINGS_DOCS.studio))
            .then((snap) => snap, (err) => {
              console.warn('[useSettings] saveStudio: could not ask the server whether this studio has a '
                + 'record, so only the changed setting is sent:', err);
              return null;
            });
          if (answer?.exists()) serverRecord = normaliseStudio(answer.data());
          serverSaysAbsent = Boolean(answer) && !answer.exists();
        }

        // Read again rather than reused: the `await` above is a gap the effect
        // can run in, and it sets this to null when the session resolves to
        // nobody. The same refusal, for the same reason, on the far side of it.
        //
        // **This one is audible** (#349): unlike the backstop above, it is
        // reachable through the gate — sign-out landing inside the `await` —
        // so it says so, as `writes.js` does at its own two backstops.
        const base = latest.current;
        if (!base) {
          console.warn(
            '[useSettings] saveStudio: the session ended while this save was being prepared, '
            + 'so nothing was written. The patch is lost; the studio record is unchanged.',
          );
          return false;
        }

        const record = !base.stored && serverRecord ? serverRecord : base.record;
        const { next, fields } = studioSave(record, patch, base.stored || queued || !serverSaysAbsent);
        // `stored: true` unconditionally, because after this line there is a
        // document: either one was already there, or this write creates it and
        // every later save merges onto a creation queued ahead of it.
        latest.current = { record: next, stored: true };
        await setDoc(
          docRef(uid, COLLECTIONS.settings, SETTINGS_DOCS.studio),
          {
            ...fields,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        return true;
      },
    };
    // `exists` is in the list (#349): `saveStudio` derives its base from
    // the same three facts `loading` is derived from when the ref has not been
    // assigned yet, and a memo that closed over a stale `exists` would decide
    // *is this a create* from a read it no longer holds.
  }, [data, exists, hasAnswer, authLoading, uid, read.failed]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within a SettingsProvider');
  return ctx;
}
